import { computed, ref } from 'vue';
import moment from 'moment';
import { useI18n, useReportParams } from '@/composables';
import { useUrl } from '@/composables/useUrl';
import { useTableQuery } from '@/composables/useTableQuery';
import { useStore } from '@/store';
import {
  METRICS_CUSTOM_COLUMN,
  METRICS_CUSTOM_EVENT,
  METRICS_CUSTOM_EVENT_HIDDEN_REVENUE,
  METRICS_CUSTOM_EVENT_REVENUE,
} from '@/services/constants/report';

const chartSeriesData: Record<string, { yAxis?: number; color?: string; format?: string }> = {
  'reports-impressions': { yAxis: 0, color: '#e6194b' },
  'reports-visits': { yAxis: 0, color: '#3cb44b' },
  'reports-clicks': { yAxis: 0, color: '#ffe119' },
  'reports-conversions': { yAxis: 0, color: '#0082c8' },
  'reports-conversionconfirm': { yAxis: 0, color: '#f58231' },
  'reports-revenue': { yAxis: 1, color: '#911eb4' },
  'reports-conversionchargerevenue': { yAxis: 1, color: '#17becf' },
  'reports-cost': { yAxis: 1, color: '#46f0f0' },
  'reports-costbot': { yAxis: 1, color: '#20b2aa' },
  'reports-profit': { yAxis: 1, color: '#f032e6' },
  'reports-hiddenrevenue': { yAxis: 1, color: '#d2f53c' },
  'reports-conversionconfirmhiddenrevenue': { yAxis: 1, color: '#fabebe' },
  'reports-hiddenprofit': { yAxis: 1, format: '0,0.00', color: '#008080' },
  'reports-impressionsuniq': { yAxis: 0, color: '#e6beff' },
  'reports-impressionsbot': { yAxis: 0, color: '#aa6e28' },
  'reports-impressionswebview': { yAxis: 0, color: '#800000' },
  'reports-impressionssuspicious': { yAxis: 0, color: '#aaffc3' },
  'reports-impressionssuspiciousblacklistedips': { yAxis: 0, color: '#808000' },
  'reports-impressionssuspiciousdatacenterips': { yAxis: 0, color: '#ffd8b1' },
  'reports-impressions_suspicious_ip_with_frequent_events': { yAxis: 0, color: '#000080' },
  'reports-impressions_suspicious_ip_with_frequent_campaign_events': { yAxis: 0, color: '#808080' },
  'reports-visitsuniq': { yAxis: 0, color: '#ff7f0e' },
  'reports-visitsbot': { yAxis: 0, color: '#1f77b4' },
  'reports-visitswebview': { yAxis: 0, color: '#2ca02c' },
  'reports-visitssuspicious': { yAxis: 0, color: '#d62728' },
  'reports-visitssuspiciousblacklistedips': { yAxis: 0, color: '#9467bd' },
  'reports-visitssuspiciousdatacenterips': { yAxis: 0, color: '#e377c2' },
  'reports-visits_suspicious_ip_with_frequent_events': { yAxis: 0, color: '#7f7f7f' },
  'reports-visits_suspicious_ip_with_frequent_campaign_events': { yAxis: 0, color: '#bcbd22' },
  'reports-clicksuniq': { yAxis: 0, color: '#17becf' },
  'reports-clicksbot': { yAxis: 0, color: '#393b79' },
  'reports-clickswebview': { yAxis: 0, color: '#637939' },
  'reports-clickssuspicious': { yAxis: 0, color: '#8c6d31' },
  'reports-clickssuspiciousblacklistedips': { yAxis: 0, color: '#843c39' },
  'reports-clickssuspiciousdatacenterips': { yAxis: 0, color: '#7b4173' },
  'reports-clicks_fast_clicker': { yAxis: 0, color: '#5254a3' },
  'reports-clicks_suspicious_ip_with_frequent_events': { yAxis: 0, color: '#6b6ecf' },
  'reports-clicks_suspicious_ip_with_frequent_campaign_events': { yAxis: 0, color: '#9c9ede' },
  'reports-conversionnew': { yAxis: 0, color: '#ff9896' },
  'reports-conversionconfirmrevenue': { yAxis: 1, color: '#c5b0d5' },
  'reports-conversiondecline': { yAxis: 0, color: '#c49c94' },
  'reports-conversioncharge': { yAxis: 0, color: '#f7b6d2' },
  'reports-conversionswebview': { yAxis: 0, color: '#ce6dbd' },
  'reports-conversionswebviewrevenue': { yAxis: 1, color: '#8c564b' },
  'reports-conversionsent': { yAxis: 0, color: '#dbdb8d' },
  'reports-cpv': { yAxis: 1, color: '#9edae5' },
  'reports-cpc': { yAxis: 1, color: '#e41a1c' },
  'reports-cpa': { yAxis: 1, color: '#377eb8' },
  'reports-ctr': { yAxis: 2, color: '#4daf4a' },
  'reports-1xctr': { yAxis: 0, color: '#984ea3' },
  'reports-uctr': { yAxis: 0, color: '#ff7f00' },
  'reports-cr': { yAxis: 2, color: '#1b9e77' },
  'reports-1xcr': { yAxis: 0, color: '#a65628' },
  'reports-cv': { yAxis: 2, color: '#f781bf' },
  'reports-1xcv': { yAxis: 0, color: '#999999' },
  'reports-roi': { yAxis: 2, color: '#66c2a5' },
  'reports-epv': { yAxis: 1, color: '#fc8d62' },
  'reports-epc': { yAxis: 1, color: '#8da0cb' },
  'reports-ap': { yAxis: 1, color: '#b3de69' },
};

export const useReportChart = () => {
  const tableQueryApi = useTableQuery();
  const store = useStore();
  const { t } = useI18n();
  const { isDashboard } = useUrl();
  const reportParamsApi = useReportParams();

  const chartOptions = ref<any>(null);

  const getCustomColumns = () => store.getCustomColumns();

  const reportMetricsName = (key: string) => {
    const customEvents = tableQueryApi.getTableCustomEvents() as Record<string, { name?: string }>;

    if (key.startsWith(METRICS_CUSTOM_EVENT)) {
      const pureKey = key.replace(METRICS_CUSTOM_EVENT, '');
      return customEvents[pureKey]?.name || key;
    }

    if (key.startsWith(METRICS_CUSTOM_EVENT_REVENUE)) {
      const pureKey = key.replace(METRICS_CUSTOM_EVENT_REVENUE, '');
      return `${customEvents[pureKey]?.name || key} (${t('revenue')})` || key;
    }

    if (key.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)) {
      const pureKey = key.replace(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE, '');
      return `${customEvents[pureKey]?.name || key} (${t('Hidden Revenue')})` || key;
    }

    if (key.startsWith(METRICS_CUSTOM_COLUMN)) {
      return getCustomColumns()[key.slice(METRICS_CUSTOM_COLUMN.length)]?.name;
    }

    return t(`metric.${key}`);
  };

  const aggregatedValueRange = computed(() => {
    if (isDashboard.value) {
      return {
        date: store.dashboardQuery.date,
        from: store.dashboardQuery.from,
        to: store.dashboardQuery.to,
      };
    }
    if (reportParamsApi.reportParams.value.compareEnabled) {
      const query = tableQueryApi.tableQueryForCurrentTab.value;
      const reportDaysDiff = moment(query.to as string).diff(query.from as string, 'hours');
      const compareReportDaysDiff = moment(query.compareTo as string).diff(
        query.compareFrom as string,
        'hours',
      );

      if (compareReportDaysDiff > reportDaysDiff) {
        return {
          date: query.compareDate,
          from: query.compareFrom,
          to: query.compareTo,
        };
      }
    }
    return {
      date: tableQueryApi.tableQueryForCurrentTab.value.date,
      from: tableQueryApi.tableQueryForCurrentTab.value.from,
      to: tableQueryApi.tableQueryForCurrentTab.value.to,
    };
  });

  const aggregatedValue = computed(() => {
    const { to, from, date = 'today' } = aggregatedValueRange.value;
    switch (date) {
      case 'last24hours':
      case 'last48hours':
      case 'today':
      case 'yesterday':
        return 'hour';
      case 'last3days':
      case 'last7days':
      case 'last30days':
      case 'thisMonth':
      case 'lastMonth':
        return 'day';
      case 'maxAvailable':
      case 'customDateTime': {
        const daysDiff = Math.abs(moment(to as string).diff(from as string, 'days'));
        if (daysDiff > 62) {
          return 'month';
        }
        if (daysDiff <= 1) {
          return 'hour';
        }
        return 'day';
      }
      default:
        return '';
    }
  });

  const aggregatedValuePlusS = computed(() => {
    switch (aggregatedValue.value) {
      case 'hour':
        return 'hours';
      case 'day':
        return 'days';
      case 'month':
        return 'months';
      default:
        return undefined;
    }
  });

  const pointInterval = computed(() => {
    switch (aggregatedValue.value) {
      case 'hour':
        return 3600000;
      case 'day':
      case 'month':
        return undefined;
      default:
        return undefined;
    }
  });

  const pointIntervalUnit = computed(() => {
    switch (aggregatedValue.value) {
      case 'hour':
        return undefined;
      case 'day':
        return 'day';
      case 'month':
        return 'month';
      default:
        return undefined;
    }
  });

  const addTime = computed(() => {
    const toValue = ({ to, date }: { to: string; date: string }) => {
      switch (date) {
        case 'thisMonth':
          return moment().endOf('month').format('YYYY-MM-DD HH:mm:ss');
        case 'today':
        case 'yesterday':
        case 'last24hours':
        case 'last48hours':
        case 'last3days':
        case 'last7days':
        case 'last30days':
        case 'lastMonth':
        case 'customDateTime':
        case 'maxAvailable':
          return to;
        default:
          return to;
      }
    };

    const getPointsAmount = ({ from, to, date }: { from: string; to: string; date: string }) =>
      moment(toValue({ to, date })).diff(from, aggregatedValuePlusS.value as moment.unitOfTime.Diff);

    const query = tableQueryApi.tableQueryForCurrentTab.value;
    const reportDiff = getPointsAmount({
      from: query.from as string,
      to: query.to as string,
      date: query.date as string,
    });

    const compareReportDiff = getPointsAmount({
      from: query.compareFrom as string,
      to: query.compareTo as string,
      date: query.compareDate as string,
    });

    return {
      report: {
        amount: reportDiff < compareReportDiff ? compareReportDiff - reportDiff : 0,
        unit: aggregatedValuePlusS.value,
      },
      compare: {
        amount: compareReportDiff < reportDiff ? reportDiff - compareReportDiff : 0,
        unit: aggregatedValuePlusS.value,
      },
    };
  });

  const currentChartHiddenSeries = computed(() =>
    isDashboard.value
      ? tableQueryApi.tableChartHiddenSeriesDashboard.value
      : tableQueryApi.tableChartHiddenSeries.value,
  );

  const isAllChartHidden = computed(() =>
    Object.values(currentChartHiddenSeries.value || {}).every((item) => item === true),
  );

  const getInitialChartOptions = () => {
    const options: any = {
      chart: {
        animation: false,
        type: 'spline',
        marginRight: 80,
        marginTop: 30,
        backgroundColor: 'var(--color-white)',
        style: {
          fontFamily: 'Lato, Helvetica, sans-serif, -apple-system',
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      tooltip: {
        crosshairs: true,
        shared: true,
      },
      plotOptions: {},
      series: [],
      xAxis: [],
      yAxis: [
        {
          title: {},
        },
        {
          labels: {
            format: '{value}',
          },
          title: {
            align: 'high',
            offset: 0,
            rotation: 0,
            y: -15,
            x: 15,
            text: isDashboard.value
              ? store.dashboardQuery.currency
              : reportParamsApi.reportParams.value.currency,
          },
          opposite: true,
        },
        {
          labels: {
            format: '{value}%',
          },
          title: {
            text: 'Percentage',
          },
        },
      ],
    };

    options.xAxis.push({
      type: 'datetime',
      title: {
        text:
          isDashboard.value || !reportParamsApi.reportParams.value.compareEnabled ? '' : 'Main data',
      },
      dateTimeLabelFormats: {
        millisecond: '%H:%M:%S.%L',
        second: '%H:%M:%S',
        minute: '%H:%M',
        hour: '%H:%M',
        day: '%e. %b',
        week: '%e. %b',
        month: '%b \'%y',
        year: '%Y',
      },
    });

    if (reportParamsApi.reportParams.value.compareEnabled) {
      options.xAxis.push({
        type: 'datetime',
        opposite: true,
        title: {
          text: 'Compare data',
        },
        dateTimeLabelFormats: {
          millisecond: '%H:%M:%S.%L',
          second: '%H:%M:%S',
          minute: '%H:%M',
          hour: '%H:%M',
          day: '%e. %b',
          week: '%e. %b',
          month: '%b \'%y',
          year: '%Y',
        },
      });
    }

    return options;
  };

  const getYAxisSuffix = (key: string) => {
    const currency = ` ${
      isDashboard.value
        ? store.dashboardQuery.currency
        : tableQueryApi.tableQueryForCurrentTab.value.currency
    }`;

    if (key.startsWith(METRICS_CUSTOM_EVENT)) {
      return '';
    }

    if (key.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)) {
      return currency;
    }

    if (key.startsWith(METRICS_CUSTOM_EVENT_REVENUE)) {
      return currency;
    }

    if (key.startsWith(METRICS_CUSTOM_COLUMN)) {
      switch (getCustomColumns()[key.replace(METRICS_CUSTOM_COLUMN, '')]?.format) {
        default:
        case 'number':
          return '';
        case 'currency':
          return currency;
        case 'percent':
          return '%';
      }
    }

    switch (chartSeriesData[key]?.yAxis) {
      default:
      case 0:
        return '';
      case 1:
        return currency;
      case 2:
        return '%';
    }
  };

  const getYAxis = (key: string) => {
    if (key.startsWith(METRICS_CUSTOM_COLUMN)) {
      switch (getCustomColumns()[key.replace(METRICS_CUSTOM_COLUMN, '')]?.format) {
        default:
        case 'number':
          return 0;
        case 'currency':
          return 1;
        case 'percent':
          return 2;
      }
    }

    if (key.startsWith(METRICS_CUSTOM_EVENT)) {
      return 0;
    }

    if (key.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)) {
      return 1;
    }

    if (key.startsWith(METRICS_CUSTOM_EVENT_REVENUE)) {
      return 1;
    }

    return chartSeriesData[key]?.yAxis;
  };

  const getColor = (key: string) => {
    const textToColorHex = (text: string) => {
      let hash = 0;
      for (let index = 0; index < text.length; index = index + 1) {
        hash = text.charCodeAt(index) + ((hash << 5) - hash);
      }

      const hue = Math.abs(hash) % 360;
      const saturation = 65;
      const lightness = 45;

      const hslToHex = (h: number, s: number, l: number) => {
        const saturationNorm = s / 100;
        const lightnessNorm = l / 100;

        const k = (n: number) => (n + h / 30) % 12;
        const a = saturationNorm * Math.min(lightnessNorm, 1 - lightnessNorm);
        const f = (n: number) =>
          Math.round(
            255 *
              (lightnessNorm -
                a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))),
          );

        return `#${f(0).toString(16).padStart(2, '0')}${f(8).toString(16).padStart(2, '0')}${f(4).toString(16).padStart(2, '0')}`;
      };

      return hslToHex(hue, saturation, lightness);
    };

    if (key.startsWith(METRICS_CUSTOM_EVENT)) {
      return textToColorHex(key);
    }

    if (key.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)) {
      return textToColorHex(key);
    }

    if (key.startsWith(METRICS_CUSTOM_EVENT_REVENUE)) {
      return textToColorHex(key);
    }

    if (key.startsWith(METRICS_CUSTOM_COLUMN)) {
      return textToColorHex(key);
    }

    return chartSeriesData[key]?.color;
  };

  const setChartHiddenSeries = (data: { id: string; value: boolean }) => {
    if (isDashboard.value) {
      store.setTableChartHiddenSeriesDashboard(data);
      return;
    }
    store.setTableChartHiddenSeries(data);
  };

  const handleChartResponse = ({
    data,
    isCompare,
  }: {
    data: Record<string, unknown>;
    isCompare: boolean;
  }) => {
    const series = data.series as string[];
    const payload = { ...data };
    delete payload.series;

    if (isCompare) {
      chartOptions.value.xAxis[1] = {
        ...chartOptions.value.xAxis[1],
        max: Number(
          moment
            .utc(series[series.length - 1])
            .add(
              addTime.value.compare.amount,
              addTime.value.compare.unit as moment.DurationInputArg2,
            )
            .format('x'),
        ),
      };
    } else {
      chartOptions.value.xAxis[0] = {
        ...chartOptions.value.xAxis[0],
        max: reportParamsApi.reportParams.value.compareEnabled
          ? Number(
              moment
                .utc(series[series.length - 1])
                .add(
                  addTime.value.report.amount,
                  addTime.value.report.unit as moment.DurationInputArg2,
                )
                .format('x'),
            )
          : null,
      };
    }

    Object.keys(payload).forEach((key) => {
      const id = isCompare ? `compare_${key}` : `id_${key}`;

      if (!isCompare) {
        setChartHiddenSeries({
          id,
          value: !!currentChartHiddenSeries.value[id],
        });
      }

      chartOptions.value.series.push({
        ...chartSeriesData[key],
        events: {
          hide: (event: { target: { options: { isCompare?: boolean; id: string } } }) => {
            if (!event.target.options.isCompare) {
              setChartHiddenSeries({
                id: event.target.options.id,
                value: true,
              });
            }
          },
          show: (event: { target: { options: { isCompare?: boolean; id: string } } }) => {
            if (!event.target.options.isCompare) {
              setChartHiddenSeries({
                id: event.target.options.id,
                value: false,
              });
            }
          },
        },
        data: payload[key],
        name: reportMetricsName(key),
        pointStart: Number(moment.utc(series[0]).format('x')),
        xAxis: isCompare ? 1 : 0,
        yAxis: getYAxis(key),
        color: getColor(key),
        pointInterval: pointInterval.value,
        pointIntervalUnit: pointIntervalUnit.value,
        isCompare,
        tooltip: {
          pointFormat: `<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}${getYAxisSuffix(key)}</b><br/>`,
        },
        id,
        ...(isCompare
          ? {
              linkedTo: `id_${key}`,
              dashStyle: 'Dot',
              name: `${reportMetricsName(key)} compare`,
            }
          : {
              visible: !currentChartHiddenSeries.value[id],
            }),
      });
    });
  };

  return {
    chartOptions,
    aggregatedValue,
    getInitialChartOptions,
    isAllChartHidden,
    handleChartResponse,
  };
};
